/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";

import { useSearchableList } from "../../../dataProvider/useSearchableList";

import {
  Box,
  Card,
  Grid,
  Hidden,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  AutocompleteInput,
  DateField,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  NumberField,
  RefreshButton,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";
import { FilterContext, FilterForm } from "ra-ui-materialui";
import { Form } from "react-final-form";

import { Pagination } from "../../../components/Pagination";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { CustomField } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { LowInventoryIcon } from "../../../components/common/LowInventoryIcon";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import { QuickFilter } from "../../../components/common/QuickFilter";
import { SearchField } from "../../../components/common/SearchField";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import ExportDropDown from "../../../components/common/ExportDropDown";
import { ListDatePicker } from "../../../components/common/ListDatePicker";
import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";
import { ageRenderer } from "../../../components/common/AgeRenderer";

import { InventoryStatus, LotTypes } from "../../../utils/constants";
import {
  exceptionRenderer,
  normalizeValue,
  piecesRenderer,
  quantityRenderer,
  totalPiecesRenderer,
  trimDecimal,
} from "../../../utils/utils";
import { getUnitName } from "../../../utils/loadTypeUtils";

// import { AuditStatusIcon } from "./AuditStatusIcon";
import { LocationsPeak } from "./inventoryItems/LocationsPeak";
import { inventoryExportCsv, inventoryExportPdf } from "./InventoriesExports";

const useStyles = makeStyles((theme) => ({
  totals: {
    marginLeft: theme.spacing(2),
  },
  textRight: {
    textAlign: "right",
  },
}));

const useToolbarStyles = makeStyles(
  (theme) => ({
    toolbar: {
      justifyContent: "space-between",
      alignItems: "flex-start",
      paddingRight: 0,
      [theme.breakpoints.up("xs")]: {
        paddingLeft: 0,
      },
      [theme.breakpoints.down("xs")]: {
        paddingLeft: theme.spacing(2),
      },
    },
    leftControlsXs: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        paddingRight: theme.spacing(2),

        // Make FilterForm inputs span full width on mobile
        "& [class*='RaFilterForm-form']": {
          width: "100%",
        },
        "& .filter-field": {
          width: "100%",
          flexBasis: "100%",
          flexGrow: 1,
        },
        // Remove the trailing spacer that prevents full-width controls
        "& .filter-field > div:last-child": {
          display: "none",
        },
      },
    },
  }),
  { name: "InboundListToolbar" },
);

const InventorySearchField = (props) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;
  const { label, alwaysOn } = props;

  const handleSearch = (value) => {
    if (value) {
      const normalizedValue = normalizeValue(value);
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { productName: { matchPhrasePrefix: `${value}` } },
            { productName: { wildcard: `*${value}*` } },
            { productCode: { matchPhrasePrefix: `${value}` } },
            { productCode: { wildcard: `*${value}*` } },
            { lotNumber: { matchPhrasePrefix: `${value}` } },
            { lotNumber: { wildcard: `*${value}*` } },
            // cannot search by containerNumber in aggregate inventory - can be in many locations
            // TODO: create new inventoryListByLocation
            // { containerNumber: { matchPhrasePrefix: `${value}` } },
            // { containerNumber: { wildcard: `*${value}*` } },
            // { normalizedPoNumber: { matchPhrasePrefix: `${normalizedValue}` } },
            // { normalizedRailCarNumber: { matchPhrasePrefix: `${normalizedValue}` } },
            { normalizedPoNumber: { wildcard: `*${normalizedValue}*` } },
            { normalizedRailCarNumber: { wildcard: `*${normalizedValue}*` } },
            { customFields: { matchPhrasePrefix: `${value}` } },
            { customFields: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (mdDown) => {
  const commodities = window.appUser.reloadYard.activeYardCommodities.items;

  return [
    <InventorySearchField label="Search" alwaysOn />,
    <TextInput
      source="poNumber.eq"
      label="PO Number"
      variant="outlined"
      resettable
      alwaysOn={false}
      parse={(value) => value?.toUpperCase()}
    />,
    <TextInput
      source="lotNumber.eq"
      label="Lot Number"
      variant="outlined"
      resettable
      alwaysOn={false}
      parse={(value) => value?.toUpperCase()}
    />,
    <TextInput
      source="railCarNumber.eq"
      label="Car/Trl Number"
      variant="outlined"
      resettable
      alwaysOn={false}
      parse={(value) => value?.toUpperCase()}
    />,
    <SelectInput
      source="areaId.eq"
      label="Area"
      variant="outlined"
      resettable
      allowEmpty
      choices={window.appUser?.reloadYard.areas.items}
    />,
    <AutocompleteInput
      source="reloadYardCommodityId.eq"
      label="Load Type"
      variant="outlined"
      resettable
      allowEmpty
      alwaysOn={!mdDown}
      choices={commodities}
      optionText="commodity.name"
    />,
    // <QuickFilter source="quantityMismatch" label="Failed Audit" defaultValue={{ eq: true }} />,
    <QuickFilter
      source="hasException"
      label="Has Exception"
      defaultValue={{ eq: true }}
    />,
    <SelectInput
      source="lotType.eq"
      label="Lot Type"
      variant="outlined"
      resettable
      allowEmpty
      choices={LotTypes}
    />,
    <ListDatePicker
      label="Date received"
      source="receivedAt"
      filterField="receivedAt"
    />,
  ];
};

const getSortByWithScope = (selectedAccountId) => {
  const key = selectedAccountId
    ? "inventoryByStatusByAccountIdByProductByLot"
    : "inventoryByStatusByAccountByProductByLot";
  const search = selectedAccountId ? "productName" : "accountName";
  return `{"key":"${key}", "search":"${search}"}`;
};

const getDefaultFilterWithScope = (selectedAccountId) => {
  if (selectedAccountId) {
    return {
      reloadYardId: window.appUser.reloadYardId,
      statusAccountIdProductNameLotNumber: {
        beginsWith: {
          status: InventoryStatus.STORED,
          accountId: selectedAccountId,
        },
      },
    };
  }

  return {
    reloadYardId: window.appUser.reloadYardId,
    statusAccountNameProductNameLotNumber: {
      beginsWith: {
        status: InventoryStatus.STORED,
      },
    },
  };
};

const getDefaultSearchFilterWithScope = (selectedAccountId) => {
  if (selectedAccountId) {
    return {
      reloadYardId: { eq: window.appUser?.reloadYardId },
      accountId: { eq: selectedAccountId },
      status: { eq: InventoryStatus.STORED },
    };
  }

  return {
    reloadYardId: { eq: window.appUser?.reloadYardId },
    status: { eq: InventoryStatus.STORED },
  };
};

const InventoryListToolbar = (props) => {
  const {
    filters,
    actions,
    selectedAccountId,
    onAccountChange,
    onAccountsLoaded,
    xsDown,
    ...rest
  } = props;
  const classes = useToolbarStyles(props);

  return Array.isArray(filters) ? (
    <FilterContext.Provider value={filters}>
      <Toolbar className={classes.toolbar}>
        <Box
          display="flex"
          alignItems="flex-start"
          flexWrap="wrap"
          flexDirection={xsDown ? "column" : "row"}
          width={xsDown ? "100%" : undefined}
          className={xsDown ? classes.leftControlsXs : undefined}
        >
          <Form
            key={selectedAccountId || "account-all"}
            onSubmit={() => {}}
            initialValues={{ accountId: selectedAccountId || "" }}
            keepDirtyOnReinitialize
            render={() => (
              <Box
                mt={1}
                mr={xsDown ? 0 : 2}
                width={xsDown ? "100%" : undefined}
              >
                <AccountDropDown
                  source="accountId"
                  label="Account"
                  alwaysOn
                  helperText={false}
                  onAccountChange={onAccountChange}
                  onAccountsLoaded={onAccountsLoaded}
                />
              </Box>
            )}
          />
          <FilterForm />
        </Box>
        <span />
        {actions &&
          React.cloneElement(actions, {
            ...rest,
            filters, // IMPORTANT: FilterButton expects an array
            ...actions.props,
          })}
      </Toolbar>
    </FilterContext.Provider>
  ) : null;
};

const ListActions = (props) => {
  const { xsDown } = props;
  const { resource, total } = useListContext();

  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <ExportDropDown
        total={total}
        resource={resource}
        exporter={inventoryExportCsv}
        exporterPdf={inventoryExportPdf}
        today={true}
        filterField={"receivedAt"}
      />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const InventoryList = (props) => {
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [account, setAccount] = useState();
  const reloadYardId = window.appUser?.reloadYardId;
  const accountStorageKey = `${props.resource}_${reloadYardId}_accountId`;
  const storedAccountId = localStorage.getItem(accountStorageKey);
  const [selectedAccountId, setSelectedAccountId] = useState(
    storedAccountId || null,
  );

  const [defaultFilter, setDefaultFilter] = useState(() =>
    getDefaultFilterWithScope(selectedAccountId),
  );
  const [defaultSearchFilter, setDefaultSearchFilter] = useState(() =>
    getDefaultSearchFilterWithScope(selectedAccountId),
  );

  // Include key & search to display default sort order.
  const defaultSort = {
    field: getSortByWithScope(selectedAccountId),
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`),
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? sessionPerPage : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Inventory",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    defaultSort: defaultSort,
    resource: "inventories",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  const applyScopedDefaults = (nextAccountId) => {
    setDefaultFilter(getDefaultFilterWithScope(nextAccountId));
    setDefaultSearchFilter(getDefaultSearchFilterWithScope(nextAccountId));
    listContext.setSort(getSortByWithScope(nextAccountId), "ASC");
    listContext.setPage(1);
    const nextFilterValues = listContext.filterValues || {};
    listContext.setFilters(
      { ...nextFilterValues },
      listContext.displayedFilters,
    );
  };

  // Restore the full account object (incl. inventoryCustomFields) after a
  // page reload — AccountDropDown only emits onAccountChange on user input.
  // Must be referentially stable: it is a dependency of the accounts fetch
  // effect inside AccountDropDown.
  const handleAccountsLoaded = useCallback((accounts) => {
    const restoredId = localStorage.getItem(accountStorageKey);
    if (!restoredId) return;
    setAccount(
      (prev) => prev || accounts?.find((item) => item.id === restoredId),
    );
  }, []);

  const handleAccountChange = (selected) => {
    setAccount(selected || null);
    const nextAccountId = selected?.id || null;
    setSelectedAccountId(nextAccountId);
    if (nextAccountId) {
      localStorage.setItem(accountStorageKey, nextAccountId);
    } else {
      localStorage.removeItem(accountStorageKey);
    }
    applyScopedDefaults(nextAccountId);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Inventory" />} />
      <InventoryListToolbar
        {...props}
        filters={getListFilters(mdDown)}
        actions={<ListActions xsDown={xsDown} />}
        xsDown={xsDown}
        selectedAccountId={selectedAccountId}
        onAccountChange={handleAccountChange}
        onAccountsLoaded={handleAccountsLoaded}
      />
      <Card>
        <InventoryGrid
          account={account}
          xsDown={xsDown}
          mdDown={mdDown}
          classes={classes}
        />
        <PaginationToolbar {...props} />
      </Card>
    </ListContextProvider>
  );
};

const PaginationToolbar = (props) => {
  const classes = useStyles();
  const listContext = useListContext(props);
  const [total, setTotal] = useState("");
  var formatter = new Intl.NumberFormat("en-US");

  useEffect(() => {
    let totalsMap = {};

    if (listContext?.ids?.length > 0 && listContext?.data) {
      for (const id of listContext.ids) {
        const uom = listContext.data[id].inventoryUOM;

        if (totalsMap[uom]) {
          totalsMap[uom] += listContext.data[id].quantityOnHand;
        } else {
          totalsMap[uom] = listContext.data[id].quantityOnHand;
        }
      }
    }

    const totals = Object.keys(totalsMap)?.length;
    let totalString = "";

    if (totals >= 0) {
      totalString = "Total: ";
      const parts = [];

      for (const [key, value] of Object.entries(totalsMap)) {
        parts.push(`${formatter.format(value)} ${key}`);
      }
      totalString += parts.join(", ");
    }

    setTotal(totalString);
  }, [listContext]);

  return (
    <Grid
      container
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="flex-end"
    >
      <Hidden mdDown>
        <Grid item lg={5}>
          <Typography className={classes.totals}>
            {" "}
            {total && `${total}`}{" "}
          </Typography>
        </Grid>
      </Hidden>
      <Grid item xs={12} lg={7}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const getTotalWeight = (record) => {
  // Return null if no record is provided
  if (!record) return null;

  const weight = record.weight;

  // Return null if weight is null, undefined, or an empty string
  if (weight == null || weight === "") return null;

  // Check if quantityOnHand is provided and is not null or undefined
  const quantityOnHand = record.quantityOnHand ?? null;

  // If quantityOnHand is null, undefined, or an empty string, return null
  if (quantityOnHand == null) return null;

  // Calculate total weight if both weight and quantityOnHand are numbers (including zero)
  if (!isNaN(weight) && !isNaN(quantityOnHand)) {
    return weight * quantityOnHand;
  }

  // Return null if any condition for a valid calculation is not met
  return null;
};

const uomRenderer = (record) => {
  const inventoryUOM = record?.inventoryUOM;
  return getUnitName(inventoryUOM);
};

const InventoryGrid = ({ account, ...props }) => {
  const { classes, xsDown, mdDown } = props;
  const hasAccount = !!account?.id;
  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={false}
          />
        );
      })
    : [];
  // Show the account's custom fields by default; users can still hide them
  // per account via the column selection dialog.
  const customFieldSources = accountFields.map(
    (field, index) => `customFields[${index}]`,
  );

  return xsDown ? (
    <SimpleList
      primaryText={(record) =>
        record.account?.displayName ?? record.accountName
      }
      secondaryText={(record) =>
        quantityRenderer(record.quantityOnHand, record.inventoryUOM)
      }
      tertiaryText={(record) => record.lotNumber}
      leftIcon={exceptionRenderer}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType="edit"
    />
  ) : mdDown ? (
    <CustomizableDatagrid
      rowClick="edit"
      storageProp={`InventoryList_${account?.id}_small`}
      defaultColumns={[
        "hasException",
        "accountName",
        "productName",
        "lotNumber",
        "quantityAvailable",
        ...customFieldSources,
      ]}
    >
      {[
        <FunctionField
          source="hasException"
          label="Exceptions"
          render={exceptionRenderer}
        />,
        <ProductLinkIcon sortable={false} />,
        <FunctionField
          source="accountName"
          label="Account"
          sortable
          render={accountNameRenderer}
          {...(!hasAccount ? { sortBy: getSortByWithScope(null) } : {})}
        />,
        <TextField
          source="productName"
          label="Product"
          sortable
          {...(hasAccount ? { sortBy: getSortByWithScope(account?.id) } : {})}
        />,
        <FunctionField source="pcs" label="Pieces" render={piecesRenderer} />,
        <TextField source="lotNumber" label="Lot Number" sortable={true} />,
        <TextField source="poNumber" label="PO Number" sortable={true} />,
        <NumberField source="quantityOnHand" label="On Hand" sortable={true} />,
        <FunctionField
          source="quantityAvailable"
          label="Available"
          headerClassName={classes.textRight}
          sortable={false}
          render={(record) =>
            trimDecimal(record.quantityOnHand - record.quantityCommitted, 3)
          }
        />,
        // <AuditStatusIcon label="Audit" sortable={false} />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  ) : (
    <CustomizableDatagrid
      rowClick="edit"
      expand={<LocationsPeak />}
      storageProp={`InventoryList_${account?.id}_large`}
      defaultColumns={[
        "hasException",
        "accountName",
        "productCode",
        "productName",
        "lotNumber",
        "receivedAt",
        "quantityOnHand",
        "quantityAvailable",
        "lowAlert",
        ...customFieldSources,
      ]}
    >
      {[
        <FunctionField
          source="hasException"
          label="Exceptions"
          render={exceptionRenderer}
        />,
        <ProductLinkIcon sortable={false} />,
        <FunctionField
          source="accountName"
          label="Account"
          sortable
          render={accountNameRenderer}
          {...(!hasAccount ? { sortBy: getSortByWithScope(null) } : {})}
        />,
        <TextField source="productCode" label="Product Code" sortable={true} />,
        <TextField
          source="productName"
          label="Product"
          sortable
          {...(hasAccount ? { sortBy: getSortByWithScope(account?.id) } : {})}
        />,
        <FunctionField source="pcs" label="Pieces" render={piecesRenderer} />,
        <FunctionField
          source="totalPcs"
          label="Total Pieces"
          headerClassName={classes.textRight}
          sortable={false}
          render={totalPiecesRenderer}
        />,
        <TextField source="lotNumber" label="Lot Number" sortable={true} />,
        <TextField source="poNumber" label="PO Number" sortable={true} />,
        <TextField
          source="railCarNumber"
          label="Car/Trl. No."
          sortable={false}
        />,
        <DateField source="receivedAt" label="Received" sortable={true} />,
        <FunctionField
          source="turnDays"
          label="Age"
          sortable={false}
          render={ageRenderer}
        />,
        <NumberField
          source="quantityOnHand"
          label="Qty. OnHand"
          sortable={true}
        />,
        <NumberField
          source="quantityCommitted"
          label="Qty. Committed"
          sortable
        />,
        <FunctionField
          source="quantityAvailable"
          label="Available"
          headerClassName={classes.textRight}
          sortable={false}
          render={(record) =>
            trimDecimal(record.quantityOnHand - record.quantityCommitted, 3)
          }
        />,
        <FunctionField
          source="inventoryUOM"
          label="UOM"
          render={uomRenderer}
        />,
        <FunctionField
          source="weight"
          label="Weight"
          headerClassName={classes.textRight}
          render={(record) =>
            quantityRenderer(getTotalWeight(record), record.weightUOM)
          }
        />,
        // <AuditStatusIcon label="Audit" sortable={false} />,
        <LowInventoryIcon source="lowAlert" label="Low" sortable={false} />,
        ...customGridFields,
        <EditButton />,
      ]}
    </CustomizableDatagrid>
  );
};
